/**
 * @voltxampere/presets — RC power-decoupling (Sprint 50-3)
 *
 * Standard digital IC power decoupling: bulk electrolytic + ceramic
 * bypass on each Vcc pin. The 100nF ceramic absorbs high-frequency
 * switching noise; the 10µF electrolytic supplies short-duration
 * current spikes when many gates switch simultaneously.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const rcPowerDecoupling = {
    id: "rc-power-decoupling",
    name: { tr: "RC Güç Filtresi (Decoupling)", en: "RC Power-Decoupling" },
    category: "power",
    difficulty: 2,
    description: {
        tr: "Dijital IC güç hattında 10µF + 100nF bypass kondansatörleri ile gürültü filtreleme.",
        en: "Digital IC power-rail decoupling: 10µF bulk + 100nF ceramic bypass.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "RC Power-Decoupling",
        description: "VCC bypass network",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc_raw" }),
            makeNet({ name: "vcc_clean" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_vcc_raw", neg: "net_gnd" },
                label: "V1",
                position: { x: -200, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R_TRACE",
                type: "resistor",
                params: { resistance: 0.1 },
                terminals: { p1: "net_vcc_raw", p2: "net_vcc_clean" },
                label: "R_TRACE",
                position: { x: -80, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C_BULK",
                type: "capacitor",
                params: { capacitance: 10e-6 },
                terminals: { p1: "net_vcc_clean", p2: "net_gnd" },
                label: "C_BULK",
                position: { x: 0, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "C_BYPASS",
                type: "capacitor",
                params: { capacitance: 100e-9 },
                terminals: { p1: "net_vcc_clean", p2: "net_gnd" },
                label: "C_BYPASS",
                position: { x: 80, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "R_LOAD",
                type: "resistor",
                params: { resistance: 100 },
                terminals: { p1: "net_vcc_clean", p2: "net_gnd" },
                label: "R_LOAD",
                position: { x: 160, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -200, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            {
                id: "w_vcc_raw",
                netId: "net_vcc_raw",
                path: [
                    { x: -200, y: -10 },
                    { x: -200, y: -40 },
                    { x: -100, y: -40 },
                    { x: -100, y: 0 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_vcc_clean",
                netId: "net_vcc_clean",
                path: [
                    { x: -60, y: 0 },
                    { x: 0, y: 0 },
                    { x: 80, y: 0 },
                    { x: 160, y: 0 },
                    { x: 160, y: -20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd",
                netId: "net_gnd",
                path: [
                    { x: -200, y: 10 },
                    { x: -200, y: 71 },
                    { x: 160, y: 71 },
                    { x: 160, y: 20 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "vcc_clean",
            analysis: "dc",
            value: 5,
            tolerance: 0.01,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Bulk + bypass kondansatör birlikte geniş frekans bandını süzer.",
            en: "Bulk + bypass caps together cover a wide noise frequency band.",
        },
        {
            tr: "Düşük ESR ceramic + yüksek kapasiteli electrolytic eşleşmesi.",
            en: "Low-ESR ceramic + high-value electrolytic complement each other.",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, §6.5"],
};
//# sourceMappingURL=rc-power-decoupling.js.map